/* eslint-disable eqeqeq */
import React, {useEffect, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {resetAlert} from "../../store/actions";
import {
  ButtonTableBulkDelete,
  CheckboxInput,
  ColorInput,
  FormGroup,
  FormGroupInput,
  Icon,
  InputError,
  Label,
  RadioInput,
  TextInput
} from "../../components";
import {getInputErrorMessage, getNameFromPreSignedUrl} from "../../utils";
import CollapseExpanded from "../CollapseExpanded";
import ImageIcon from "../../assets/icons/icon-image-inspection.svg";
import PlayVideoIcon from "../../assets/icons/icon-video.svg";
import {hasErrors} from "../../utils/group-option-utils";
import * as pdfjslib from "pdfjs-dist/webpack";
import {renderPdfToImage} from "../../utils/common-utils";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import {FFMPEG_CORE_LIBS} from "../../constants/routes";

function InspectionForm({ setInputs, modalData, errorMessages = {} }) {
  const dispatch = useDispatch();

  const colorDefault = "#000000";
  const maxFileNameDisplay = 30;
  const maxFileSize = 2 * 1024 * 1024; // 2MB
  const allowedFileTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
  const maxVideoSize = 10 * 1024 * 1024; // 10MB
  const allowedVideoTypes = ['video/mp4', 'video/avi', 'video/mov', 'video/flv', 'video/webm'];

  const [data, setData] = useState(modalData);
  const [titleColor, setTitleColor] = useState(modalData.title_color || colorDefault);
  const [descriptionColor, setDescriptionColor] = useState(modalData.description_color || colorDefault);
  const [isExpanded1, setExpanded1] = useState(true);
  const [isExpanded2, setExpanded2] = useState(true);
  const [isExpanded3, setExpanded3] = useState(true);
  const [isExpanded4, setExpanded4] = useState(true);
  const [isExpanded5, setExpanded5] = useState(true);
  const [isExpanded6, setExpanded6] = useState(true);
  const [imageInfo, setImageInfo] = useState(null);
  const [selectedFileImage, setSelectedFileImage] = useState(null);
  const [pdfImageFile, setPdfImageFile] = useState(null);
  const [videoInfo, setVideoInfo] = useState(null);
  const [selectedFileVideo, setSelectedFileVideo] = useState(null);
  const imageInputRef = useRef();
  const videoInputRef = useRef();

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!allowedFileTypes.includes(file.type)) {
        alert('拡張子がJPEG,PNG,GIF,PDF以外のファイルをアップロードすることは出来ません'); return;
      }
      if (file.size > maxFileSize) {
        alert('ファイルの合計サイズが2MBを超えています。'); return;
      }
      if (file.type === 'application/pdf') {
        const fileReader = new FileReader();
        fileReader.onload = async function() {
          const typedArray = new Uint8Array(this.result);
          const pdf = await pdfjslib.getDocument(typedArray).promise;
          const pdfImageFileName = file.name.replace(".pdf", "");
          const getFileImagePDF = await renderPdfToImage(pdf, pdfImageFileName);
          setPdfImageFile(getFileImagePDF);
          setSelectedFileImage(file);
          setFormData('delete_image', false);
          setFormData("remark_image_pdf", file);
          setFormData("remark_image", getFileImagePDF);
          setData({
            ...data,
            [event.target.name]: 'favicon.ico'
          });
        };
        fileReader.readAsArrayBuffer(file);
      } else {
        setSelectedFileImage(file);
        setPdfImageFile(null);
        setFormData('delete_image', false);
        setFormData("remark_image_pdf", null);
        setFormData("remark_image", file);
        setData({
          ...data,
          [event.target.name]: 'favicon.ico'
        });
      }
    }
  };

  const handleVideoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!allowedVideoTypes.includes(file.type)) {
        alert('拡張子がMP4,AVI,MPEG,HEVC以外のファイルをアップロードすることは出来ません'); return;
      }
      if (file.size > maxVideoSize) {
        alert('備考動画アップロード可能な動画の合計容量（10MB）を超えています。'); return;
      }
      setFormData('delete_video', false);
      setFormData("remark_video", file);
      setSelectedFileVideo(file);
      setData({
        ...data,
        [event.target.name]: event.target.value
      });
    }
  };

  const generateVideoThumbnail = async () => {
    if (selectedFileVideo) {
      setVideoInfo(null);
      const fileExtension = selectedFileVideo.name.split(".").pop().toLowerCase();
      if (fileExtension === "avi") {
        await setAviVideoFile(selectedFileVideo);
      } else {
        const videoUrl = URL.createObjectURL(selectedFileVideo);
        handleSetThumbnail(videoUrl, 'video');
      }
    } else if (data.remark_video) {
      setVideoInfo({
        url: data.remark_video_thumbnail,
        name: getNameFromPreSignedUrl(modalData.remark_video)
      });
    } else {
      setVideoInfo(null);
    }
  };

  const handleSetThumbnail = (fileUrl, type) => {
    if (type === "video") {
      const video = document.createElement("video");
      video.src = fileUrl;
      video.crossOrigin = "anonymous"; // Đảm bảo CORS nếu cần
      video.preload = "metadata";
      video.addEventListener("loadeddata", () => {
        video.currentTime = 1;
      });
      video.addEventListener("seeked", () => {
        const canvas = document.createElement("canvas");
        canvas.width = 70;
        canvas.height = 70;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const thumbnailUrl = canvas.toDataURL("image/png");
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const thumbnailFile = new File([blob], selectedFileVideo.name, { type: "image/png" });
              setFormData('remark_video_thumbnail', thumbnailFile);
            }
          },
          "image/png",
          1.0
        );
        setVideoInfo({url: thumbnailUrl, name: selectedFileVideo.name});
      });
    }
    if (type === "image") {
      const img = new Image();
      img.src = fileUrl;
      img.crossOrigin = "anonymous";

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = 70;
        canvas.height = 70;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const thumbnailFile = new File([blob], selectedFileImage.name, { type: "image/png" });
              setFormData('remark_image_thumbnail', thumbnailFile);
            }
          },
          "image/png",
          1.0
        );
      };
    }
  }

  const setAviVideoFile = async (file) => {
    try {
      const ffmpeg = new FFmpeg();
      if (!ffmpeg.loaded) {
        await ffmpeg.load({
          coreURL: FFMPEG_CORE_LIBS.replace(':filename', 'ffmpeg-core.js'),
          wasmURL: FFMPEG_CORE_LIBS.replace(':filename', 'ffmpeg-core.wasm'),
        });
      }

      const fileData = await file.arrayBuffer();
      await ffmpeg.writeFile(file.name, new Uint8Array(fileData));

      await ffmpeg.exec([
        '-i', file.name,
        '-ss', '00:00:01',
        '-vframes', '1',
        '-s', '70x70',
        'thumbnail.png'
      ]);

      const outputData = await ffmpeg.readFile('thumbnail.png');
      const thumbnailBlob = new Blob([outputData.buffer], { type: 'image/png' });
      const thumbnailURL = URL.createObjectURL(thumbnailBlob);
      const thumbnailFile = new File([thumbnailBlob], selectedFileVideo.name, { type: "image/png" });
      setFormData('remark_video_thumbnail', thumbnailFile);
      setVideoInfo({url: thumbnailURL, name: selectedFileVideo.name});
    } catch (error) {
      console.log(error);
    }
  };

  const remarkMediaClick = (type) => {
    switch (type) {
      case "video":
        videoInputRef.current.click();
        break;
      case "image":
        imageInputRef.current.click();
        break;
      default:
        return;
    }
  };

  const removeMedia = (type) => {
    switch (type) {
      case "video":
        setData({ ...data, remark_video: null });
        setFormData('delete_video', true);
        setFormData("remark_video", null);
        setFormData("remark_video_thumbnail", null);
        setVideoInfo(null);
        setSelectedFileVideo(null);
        break;
      case "image":
        setData({ ...data, remark_image: null, remark_image_pdf: null });
        setImageInfo(null);
        setSelectedFileImage(null);
        setPdfImageFile(null);
        setFormData('delete_image', true);
        setFormData("remark_image_pdf", null);
        setFormData("remark_image", null);
        setFormData("remark_image_thumbnail", null);
        break;
      default:
        return;
    }
  };

  const getFileNameDisplay = (fileName) => {
    const extensionIdx = fileName.lastIndexOf(".");
    const extension = fileName.substring(extensionIdx);
    if (fileName.length > maxFileNameDisplay) {
      fileName = fileName.substring(0, maxFileNameDisplay - 3) + "..." + extension;
    }
    return fileName;
  };

  const generateImageUrl = () => {
    if (selectedFileImage) {
      if (selectedFileImage.type === 'application/pdf') {
        handleSetThumbnail(URL.createObjectURL(pdfImageFile), 'image');
        setImageInfo({url: URL.createObjectURL(pdfImageFile), name: selectedFileImage.name});
      } else {
        handleSetThumbnail(URL.createObjectURL(selectedFileImage), 'image');
        setImageInfo({url: URL.createObjectURL(selectedFileImage), name: selectedFileImage.name});
      }
    } else if (data.remark_image) {
      setImageInfo(
        {
          url: data.remark_image_thumbnail,
          name: getNameFromPreSignedUrl(data.remark_image_pdf ? data.remark_image_pdf : data.remark_image)
        }
      );
    } else {
      setImageInfo(null);
    }
  };

  useEffect(() => {
    generateImageUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFileImage, data]);

  useEffect(() => {
    generateVideoThumbnail().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFileVideo]);

  useEffect(() => {
    setFormData("title_color", titleColor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titleColor]);
  useEffect(() => {
    setFormData("description_color", descriptionColor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descriptionColor]);

  // Function to handle changes in input
  const onChange = (e, fieldName) => {
    dispatch(resetAlert());
    let value = e.target.value;
    setData((preData) => {
      const updatedData = {...preData};
      if (fieldName === 'has_unit') {
        value = !updatedData[fieldName];
      }
      if (fieldName === "inspection_item_type") {
        if (value === 'input') {
          updatedData.inspection_judgment.input_value_type = 1;
        }
        updatedData[fieldName].name = value;
      } else {
        updatedData[fieldName] = value;
      }
      return updatedData;
    });
    setFormData(fieldName, value);
  };

  const getDefaultBoundaryType = (value) => {
    if (value === undefined || value === null) {
      return 1;
    }
    return value;
  }

  // Function to handle changes in input
  const onJudgmentChange = (e, fieldName) => {
    dispatch(resetAlert());
    let value = e.target.value;
    setData((preData) => {
      const updatedData = {...preData};
      let updateValue = value;
      if (fieldName === "has_value_limit") {
        updateValue = value === "yes";
        if (value === "yes") {
          updatedData.inspection_judgment.input_value_type = 1;
          const upper_boundary_type = getDefaultBoundaryType(updatedData.inspection_judgment?.upper_boundary_type);
          updatedData.inspection_judgment.upper_boundary_type = upper_boundary_type;
          setFormData('upper_boundary_type', upper_boundary_type);
          const lower_boundary_type = getDefaultBoundaryType(updatedData.inspection_judgment?.lower_boundary_type);
          updatedData.inspection_judgment.lower_boundary_type = lower_boundary_type;
          setFormData('lower_boundary_type', lower_boundary_type);
        }
      } else if (['lower_boundary_type', 'upper_boundary_type', 'input_value_type'].includes(fieldName)) {
        updateValue = parseInt(value);
      }
      updatedData.inspection_judgment[fieldName] = updateValue;
      return updatedData;
    });
    setFormData(fieldName, value);
  };

  const setFormData = (fieldName, value) => {
    setInputs((prevValues) => {
      const updatedData = {...prevValues};
      updatedData[fieldName] = value;
      return updatedData;
    });
  }

  //Function help display multiple error message
  const getMultipleError = (names) => {
    if (errorMessages.errors === undefined || errorMessages.errors.length === 0) {
      return "";
    }
    const filterErrors = Object.keys(errorMessages.errors)
      .filter(key => names.includes(key))
      .reduce((obj, key) => {
        obj[key] = errorMessages.errors[key];
        return obj;
      }, {});

    return Object.keys(filterErrors).map((key, idx) => {
      const value = filterErrors[key];
      return idx < Object.keys(filterErrors).length -1 ? value + "\n" : value;
    });
  }

  const getDigitsInput = (value) => {
    if (value === undefined || value === null) {
      return '';
    }
    return value;
  }

  return (
    <div className="flex flex-col gap-[16px]">
      <div className="flex flex-col px-[10px] py-[14px] bg-white rounded-lg border border-[#D0D5DD]">
        <CollapseExpanded title="点検項目" isExpanded={isExpanded1} setExpanded={setExpanded1}>
          <div className="flex flex-col gap-[16px] mt-[16px] border-t border-[#D0D5DD] pl-[28px] pt-[16px]">
            <FormGroup direction="horizontal" className="mb-0">
              <Label text='点検項目テキスト' className="mr-[44px] w-[132px] inline-block flex-shrink-0"/>
              <FormGroupInput error={getInputErrorMessage(errorMessages.errors, "title")} className="flex-col">
                <TextInput type="text" autoFocus maxLength={40}
                           value={data.title || ''} name="title"
                           hasError={hasErrors("title", errorMessages.errors)}
                           onChange={(e) => {onChange(e, "title")}}
                           className="w-full inline-block" />
              </FormGroupInput>
            </FormGroup>
            <FormGroup direction="horizontal" className="mb-0">
              <Label text='文字色' className="mr-[44px] w-[132px] inline-block flex-shrink-0"/>
              <FormGroupInput error={getInputErrorMessage(errorMessages.errors, "title_color")} className="flex-col">
                <ColorInput styles="translate-x-20 -translate-y-1/2 top-[20px]"
                            color={titleColor}
                            setColor={setTitleColor}
                            itemName="title_color"
                            hasError={hasErrors( 'title_color', errorMessages.errors)}
                            width="text-tenken-tablet-regular xl:text-base" />
              </FormGroupInput>
            </FormGroup>
          </div>
        </CollapseExpanded>
      </div>
      <div className="flex flex-col px-[10px] py-[14px] bg-white rounded-lg border border-[#D0D5DD]">
        <CollapseExpanded title="点検結果" isExpanded={isExpanded2} setExpanded={setExpanded2}>
          <div className="flex flex-col mt-[16px] border-t border-[#D0D5DD] pl-[28px] pt-[16px]">
            <FormGroup direction="horizontal" className="mb-0 gap-[44px]">
              <Label text="入力方式" htmlFor="" className="w-[132px] inline-block" />
              <FormGroupInput error={getMultipleError(["inspection_item_type", "in_use"])} className="flex-col mt-[5px]">
                <div className="flex flex-row">
                  <RadioInput name="inspection_item_type"
                              id="inspection_item_type-input"
                              value="input" isDisabled={!!modalData?.is_using}
                              onChange={(e) => onChange(e, "inspection_item_type")}
                              checked={data.inspection_item_type.name === "input"}
                              label="テキスト" />
                  <RadioInput name="inspection_item_type"
                              id="inspection_item_type-checkbox"
                              value="checkbox" isDisabled={!!modalData?.is_using}
                              onChange={(e) => onChange(e, "inspection_item_type")}
                              checked={data.inspection_item_type && data.inspection_item_type.name === "checkbox"}
                              className="ml-[32px]"
                              label="選択" />
                </div>
              </FormGroupInput>
            </FormGroup>
            {modalData?.is_using != true &&
            <p className="ml-[176px] text-red-500 text-sm mt-[2px] whitespace-pre-wrap">この項目は、一度でも点検テンプレートが使用されると編集不可能になります。</p>}
          </div>
        </CollapseExpanded>
      </div>
      <div className="flex flex-col px-[10px] py-[14px] bg-white rounded-lg border border-[#D0D5DD]">
        <CollapseExpanded title="判定設定" isExpanded={isExpanded3} setExpanded={setExpanded3}>
          <div className="flex flex-col gap-[16px] mt-[16px] border-t border-[#D0D5DD] pl-[28px] pt-[16px]">
            <FormGroup direction="horizontal" className="mb-0">
              <Label text="判断基準テキスト" htmlFor="description" className="mr-[44px] w-[132px] inline-block flex-shrink-0" />
              <FormGroupInput error={getInputErrorMessage(errorMessages.errors, "description")} className="flex-col">
                <TextInput type="text" name="description" id="description"
                           value={data.description || ''} maxLength={30}
                           onChange={(e) => onChange(e, "description")}
                           hasError={hasErrors( 'description', errorMessages.errors)}
                           className="w-full text-tenken-tablet-regular xl:text-base inline-block" />
              </FormGroupInput>
            </FormGroup>
            <FormGroup direction="horizontal" className="mb-0">
              <Label text="文字色" htmlFor="description_color" className="mr-[44px] w-[132px] inline-block" />
              <FormGroupInput error={getInputErrorMessage(errorMessages.errors, "description_color")} className="flex-col">
                <ColorInput styles="translate-x-20 -translate-y-1/2 top-[20px]"
                            color={descriptionColor}
                            setColor={setDescriptionColor}
                            hasError={hasErrors( 'description_color', errorMessages.errors)}
                            itemName="description_color"
                            width="text-tenken-tablet-regular xl:text-base" />
              </FormGroupInput>
            </FormGroup>
            {data.inspection_item_type.name === "input" && (
              <>
                <CollapseExpanded title="規格" isExpanded={isExpanded5} setExpanded={setExpanded5}>
                  <div className="flex flex-col gap-[16px] mt-[16px] border-t border-[#D0D5DD] pl-[28px] pt-[16px]">
                    <FormGroup direction="horizontal" className="mb-0 gap-[46px]">
                      <Label text="上下限判定" htmlFor="has_value_limit-yes" className="w-[100px] inline-block" />
                      <FormGroupInput error={getInputErrorMessage(errorMessages.errors, "has_value_limit")} className="flex-col mt-[5px]">
                        <div className="flex items-center">
                          <RadioInput name="has_value_limit"
                                      id="has_value_limit-yes"
                                      value="yes" isDisabled={!!modalData?.is_using}
                                      onChange={(e) => onJudgmentChange(e, "has_value_limit")}
                                      checked={data.inspection_judgment?.has_value_limit === true}
                                      label="あり" />
                          <RadioInput name="has_value_limit"
                                      id="has_value_limit-no"
                                      value="no" isDisabled={!!modalData?.is_using}
                                      onChange={(e) => onJudgmentChange(e, "has_value_limit")}
                                      checked={data.inspection_judgment?.has_value_limit === false}
                                      className="ml-[32px]"
                                      label="なし" />
                        </div>
                      </FormGroupInput>
                    </FormGroup>
                    {data.inspection_judgment?.has_value_limit === true &&
                    <>
                      <FormGroup direction="horizontal" className="mb-0">
                        <Label text="判定上下限値" htmlFor="upper_value" className="mt-[45px] mr-[46px] w-[100px] inline-block"/>
                        <FormGroup direction="vertical" className="mb-0 gap-[10px]">
                          <Label text="下限" htmlFor="lower_value" className="mr-[46px] w-[100px] inline-block"/>
                          <FormGroupInput className="flex-col gap-[10px]">
                            <TextInput type="text" id="lower_value" name="lower_value"
                                       value={data.inspection_judgment?.lower_value || ''}
                                       hasError={hasErrors(["lower_value", "upper_lower_value"], errorMessages.errors)}
                                       onChange={(e) => onJudgmentChange(e, "lower_value")}
                                       className="w-full max-w-[200px] text-tenken-tablet-regular xl:text-base inline-block mr-8"
                                       disabled={!!modalData?.is_using} maxLength="20" />
                          </FormGroupInput>
                          <RadioInput name="lower_boundary_type"
                                      id="lower_boundary_type-1"
                                      value="1" isDisabled={!!modalData?.is_using}
                                      onChange={(e) => onJudgmentChange(e, "lower_boundary_type")}
                                      checked={data.inspection_judgment?.lower_boundary_type === 1}
                                      label="以上"/>
                          <RadioInput name="lower_boundary_type"
                                      id="lower_boundary_type-0"
                                      value="0" isDisabled={!!modalData?.is_using}
                                      onChange={(e) => onJudgmentChange(e, "lower_boundary_type")}
                                      checked={data.inspection_judgment?.lower_boundary_type === 0}
                                      label="超"/>
                        </FormGroup>
                        <FormGroup direction="vertical" className="gap-[10px]">
                          <Label text="上限" htmlFor="upper_value" className="mr-[44px] w-[100px] inline-block"/>
                          <FormGroupInput className="flex-col gap-[10px]">
                            <TextInput type="text" id="upper_value" name="upper_value"
                                       value={data.inspection_judgment?.upper_value || ''}
                                       hasError={hasErrors( ["upper_value", "upper_lower_value"], errorMessages.errors)}
                                       onChange={(e) => onJudgmentChange(e, "upper_value")}
                                       className="w-full max-w-[200px] text-tenken-tablet-regular xl:text-base inline-block mr-8"
                                       disabled={!!modalData?.is_using} maxLength="20" />
                          </FormGroupInput>
                          <RadioInput name="upper_boundary_type"
                                      id="upper_boundary_type-1"
                                      value="1" isDisabled={!!modalData?.is_using}
                                      onChange={(e) => onJudgmentChange(e, "upper_boundary_type")}
                                      checked={data.inspection_judgment?.upper_boundary_type === 1}
                                      label="以下"/>
                          <RadioInput name="upper_boundary_type"
                                      id="upper_boundary_type-0"
                                      value="0" isDisabled={!!modalData?.is_using}
                                      onChange={(e) => onJudgmentChange(e, "upper_boundary_type")}
                                      checked={data.inspection_judgment?.upper_boundary_type === 0}
                                      label="未満"/>
                        </FormGroup>
                      </FormGroup>
                      <div className="ml-[132px] mt-[-32px]">
                        <InputError error={getMultipleError(["upper_value", "upper_boundary_type", "upper_lower_value", "lower_value", "lower_boundary_type"])} />
                      </div>
                      <div className="flex flex-row ml-[120px]">
                        <Icon name='light-bulb' className="w-[25px] h-[25px]" />
                        <p className="ml-[3px] text-sm mt-[2px] whitespace-pre-wrap">
                          システムが点検結果の入力値に対する合否判定処理に使用します。<br/>
                          単位は入力値設定の単位を自動的に表示します。</p>
                      </div>
                    </>
                    }
                  </div>
                </CollapseExpanded>
                <CollapseExpanded title="入力値設定" isExpanded={isExpanded6} setExpanded={setExpanded6}>
                  <div className="flex flex-col gap-[16px] mt-[16px] border-t border-[#D0D5DD] pl-[28px] pt-[16px]">
                    <FormGroup direction="horizontal" className="mb-0 gap-[14px]">
                      <Label text="入力文字列タイプ" htmlFor="input_type-number" className="w-[132px] inline-block" />
                      <FormGroupInput error={getInputErrorMessage(errorMessages.errors, "input_type")} className="flex-col mt-[5px]">
                        <div className="flex items-center">
                          <RadioInput name="input_value_type" id="input_type-number"
                                      value="1" isDisabled={!!modalData?.is_using}
                                      onChange={(e) => onJudgmentChange(e, "input_value_type")}
                                      checked={data.inspection_judgment?.input_value_type === 1}
                                      label="数値" />
                          <RadioInput name="input_value_type" id="input_type-text"
                                      value="2" className="ml-[32px]"
                                      onChange={(e) => onJudgmentChange(e, "input_value_type")}
                                      checked={data.inspection_judgment?.input_value_type === 2}
                                      isDisabled={!!modalData?.is_using || data.inspection_judgment?.has_value_limit === true}
                                      label="非数値" />
                        </div>
                      </FormGroupInput>
                    </FormGroup>
                    {data.inspection_judgment?.input_value_type !== 2 ?
                      <>
                        <FormGroup direction="horizontal" className="mb-0">
                          <Label text="桁数" htmlFor="integer_length"
                                 className="mr-[46px] mt-[45px] w-[100px] inline-block"/>
                          <FormGroup direction="vertical" className="gap-[10px] w-[100px]">
                            <Label text="整数部" htmlFor="integer_length" className="mr-[16px] inline-block"/>
                            <FormGroupInput className="flex-col">
                              <TextInput type="text" id="integer_length" name="integer_length"
                                         value={getDigitsInput(data.inspection_judgment?.integer_length)}
                                         hasError={hasErrors(["integer_length", "integer_decimal_length"], errorMessages.errors)}
                                         onChange={(e) => onJudgmentChange(e, "integer_length")}
                                         className="text-tenken-tablet-regular xl:text-base inline-block"
                                         disabled={!!modalData?.is_using} maxLength="6"/>
                            </FormGroupInput>
                          </FormGroup>
                          <span className="mx-[16px] mt-[58px] inline-block">.</span>
                          <FormGroup direction="vertical" className="mb-0 gap-[10px] w-[100px]">
                            <Label text="小数部" htmlFor="decimal_length" className="mr-[44px] inline-block"/>
                            <FormGroupInput className="flex-col">
                              <TextInput type="text" name="decimal_length" id="decimal_length"
                                         value={getDigitsInput(data.inspection_judgment?.decimal_length)}
                                         hasError={hasErrors(["decimal_length", "integer_decimal_length"], errorMessages.errors)}
                                         onChange={(e) => onJudgmentChange(e, "decimal_length")}
                                         className="text-tenken-tablet-regular xl:text-base inline-block"
                                         disabled={!!modalData?.is_using} maxLength="6"/>
                            </FormGroupInput>
                          </FormGroup>
                          <FormGroup direction="vertical" className="!mb-0 gap-[10px] ml-[28px]">
                            <Label text="単位" htmlFor="unit" className="inline-block"/>
                            <FormGroupInput className="flex-col">
                              <TextInput type="text" id="unit" name="unit"
                                         value={data.unit || ''}
                                         disabled={!!modalData?.is_using || !data.has_unit}
                                         hasError={hasErrors("unit", errorMessages.errors)}
                                         onChange={(e) => onChange(e, "unit")} maxLength={20}
                                         className="w-full mb-[10px] max-w-[210px] text-tenken-tablet-regular xl:text-base inline-block"/>
                              <CheckboxInput id="has_unit" name="has_unit" label="単位不要"
                                             disabled={!!modalData?.is_using}
                                             onChange={(e) => onChange(e, "has_unit")}
                                             checked={!data.has_unit} value='false'/>
                            </FormGroupInput>
                          </FormGroup>
                        </FormGroup>
                        <div className="ml-[132px] mt-[-32px]">
                          <InputError error={getMultipleError(["integer_length", "decimal_length", "integer_decimal_length", "unit"])} />
                        </div>
                        <div className="flex flex-row ml-[120px] mb-[20px]">
                          <Icon name='light-bulb' className="w-[25px] h-[25px]" />
                          <p className="text-sm mt-[2px] whitespace-pre-wrap">
                            0.00~9.99まで入力可能にする場合は整数部に「1」小数部に「2」を入力します。<br/>
                            単位を「kg」と表示する場合は単位の入力欄に「kg」を入力します。<br/>
                            単位を非表示にしたい場合は、単位不要に✓を入れます。</p>
                        </div>
                      </> :
                      <>
                        <FormGroup direction="horizontal" className="mb-0">
                          <Label text="桁数" htmlFor="max_string_length" className="mr-[46px] w-[100px] inline-block"/>
                          <FormGroupInput error={getInputErrorMessage(errorMessages.errors, "max_string_length")} className="flex-col">
                            <TextInput type="text" id="max_string_length" name="max_string_length"
                                       disabled={!!modalData?.is_using}
                                       value={data.inspection_judgment?.max_string_length || ''} maxLength={2}
                                       hasError={hasErrors("max_string_length", errorMessages.errors)}
                                       onChange={(e) => onJudgmentChange(e, "max_string_length")}
                                       className="w-full max-w-[160px] text-tenken-tablet-regular xl:text-base inline-block"/>
                          </FormGroupInput>
                        </FormGroup>
                        <div className="flex flex-row ml-[120px] mb-[20px]">
                          <Icon name='light-bulb' className="w-[25px] h-[25px]" />
                          <p className="ml-[3px] text-sm mt-[2px] whitespace-pre-wrap">
                            全角半角に関わらず文字列数をカウントします。<br/>
                            (1~50文字で設定可能です)</p>
                        </div>
                      </>
                    }
                  </div>
                </CollapseExpanded>
              </>
            )}
          </div>
        </CollapseExpanded>
      </div>
      <div className="flex flex-col px-[10px] py-[14px] bg-white rounded-lg border border-[#D0D5DD]">
        <CollapseExpanded title="留意事項" isExpanded={isExpanded4} setExpanded={setExpanded4}>
          <div className="flex flex-col gap-[16px] mt-[16px] border-t border-[#D0D5DD] pl-[28px] pt-[16px]">
            <FormGroup direction="horizontal" className="mb-0">
              <Label text="画像" htmlFor="remark_image" className="mr-[44px] w-[132px] inline-block flex-shrink-0" />
              <FormGroupInput error={getInputErrorMessage(errorMessages.errors, "remark_image")}
                              className={`flex-col w-full  ${hasErrors('remark_image', errorMessages?.errors) && ' gap-[20px]'}`}>
                <div className="flex flex-row">
                  {imageInfo ? (
                    <div className='relative'>
                      <img src={imageInfo.url} alt="remark" className="w-[70px] h-[70px] object-cover flex-shrink-0"/>
                      <ButtonTableBulkDelete type="button"
                                             className='absolute mt-[3px] ml-[12px] h-[20px]'
                                             onClick={() => removeMedia('image')} />
                    </div>
                  ) : (
                    <div className="p-[16px] bg-[#F2F4F7] rounded-lg flex-shrink-0">
                      <img src={ImageIcon} alt="icon-remark"/>
                    </div>
                  )}
                  <input type="file"
                         name="remark_image"
                         ref={imageInputRef}
                         accept=".svg,.png,.jpg,.pdf,.gif"
                         onChange={handleImageUpload}
                         className="hidden" />
                  <button type="button"
                          className="ml-[12px] px-[12px] w-full max-w-[360px] h-[70px] border border-[#EAECF0] rounded-[12px]"
                          onClick={(e) => remarkMediaClick('image')}>
                    {imageInfo ? (
                      <span className="block text-[14px] leading-5 font-light text-[#175CD3]">{getFileNameDisplay(imageInfo.name)}</span>
                    ) : (
                      <>
                        <span className="block text-[14px] leading-5 font-light text-[#175CD3]">アップロードするにはクリック</span>
                        <span className="leading-5 text-[14px] font-light">SVG, PNG, JPG, PDF or GIF (max. 800px800px)</span>
                      </>
                    )}
                  </button>
                </div>
              </FormGroupInput>
            </FormGroup>
            <FormGroup direction="horizontal" className="mb-0">
              <Label text="動画" htmlFor="remark_video" className="mr-[44px] w-[132px] inline-block flex-shrink-0" />
              <FormGroupInput error={getInputErrorMessage(errorMessages.errors, "remark_video")}
                              className={`flex-col w-full ${hasErrors('remark_video', errorMessages?.errors) && ' gap-[20px]'}`}>
                <div className="flex flex-row">
                  <div className="relative">
                    {videoInfo ? (
                      <div className='relative'>
                        <img src={videoInfo.url} alt="video" className="w-[70px] h-[70px] object-cover flex-shrink-0"/>
                      </div>
                    ) : (
                      <div className="p-[16px] bg-[#F2F4F7] rounded-lg flex-shrink-0">
                        <img src={PlayVideoIcon} alt="icon-video"/>
                      </div>
                    )}
                    {videoInfo &&
                    <ButtonTableBulkDelete type="button"
                                           className="absolute mt-[3px] ml-[12px] h-[20px] mb-[4px]"
                                           onClick={() => removeMedia('video')} />
                    }
                  </div>
                  <input type="file"
                         name="remark_video"
                         ref={videoInputRef}
                         accept=".mp4,.avi,.mpeg,.hevc"
                         onChange={handleVideoUpload}
                         className="hidden" />
                  <button type="button"
                          className="ml-[12px] px-[12px] w-full max-w-[360px] h-[70px] border border-[#EAECF0] rounded-[12px]"
                          onClick={(e) => remarkMediaClick('video')}>
                    {videoInfo ? (
                      <span className="block text-[16px] leading-5 font-light text-[#175CD3]">{getFileNameDisplay(videoInfo.name)}</span>
                    ) : (
                      <>
                        <span className="block text-[14px] leading-5 font-light text-[#175CD3]">アップロードするにはクリック</span>
                        <span className="leading-5 text-[14px] font-light">MP4, AVI, MPEG or HEVC (max. 10MB)</span>
                      </>
                    )}
                  </button>
                </div>
              </FormGroupInput>
            </FormGroup>
            <FormGroup>
              <Label text="要注意" htmlFor="remark_text" className="mr-[44px] w-[132px] inline-block flex-shrink-0" />
              <textarea id="remark_text" rows="4" name="remark_text"
                        value={data.remark_text || ''}
                        onChange={(e) => onChange(e, "remark_text")}
                        className="block 2xl:px-[14px] 2xl:py-[12px] xl:px-[12px] xl:py-[10px] h-[66px] md:px-[10px] py-[8px] w-full 2xl:text-[16px] xl:text-[15px] md:text-[14px] rounded-lg border border-[#D0D5DD] focus:ring-0 focus:border-[#D0D5DD]" />
            </FormGroup>
          </div>
        </CollapseExpanded>
      </div>
    </div>
  );
}

export default InspectionForm;